import { useEffect, useMemo } from 'react'
import { EFinancingProgram, EPaymentPlan, EProvince } from '@if/core/types'
import { UseFormSetValue } from 'react-hook-form'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { ETerms } from '@src/types/Constants'
import { EProtectionPlanCoverage } from '@src/types/CalculatorEstimateDto'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useGetPossibleTerms } from '@src/data/api/worksheet-api/worksheet-api'
import {
  useCalculateTermAndPlanOptionsSimulation,
  useCalculatorEstimate,
} from '@src/data/api/calculator-api/calculator-api'
import { useMappedTermAndPlanOptions } from '@src/containers/ViewCreditApplicationPage/worksheet-hooks'
import {
  createComputeMonthTermFrequency,
  createEstimateRequest,
  createTermPlanSimulationRequest,
  pickDefaultTermAndPlanOption,
} from './personalLoanCalculator-utils'

interface UsePossibleTermsProps {
  amountRequested?: number
  paymentPlanId?: string
  creditRatingInterestRate: number
  merchantId: string
  /** Provincial rate caps decide which terms stay legal. */
  provinceIso?: EProvince
  includeInsurance?: boolean
  isJointCoverage?: boolean
  /** The date the estimate priced on, so the terms answer for the same loan. */
  firstPaymentOn?: string
}

/**
 * Loads the legal terms for the selected plan, which decide whether the protection plan
 * can be offered on the selected term.
 */
export const usePossibleTerms = ({
  amountRequested,
  paymentPlanId,
  creditRatingInterestRate,
  merchantId,
  provinceIso,
  includeInsurance = false,
  isJointCoverage = false,
  firstPaymentOn,
}: UsePossibleTermsProps) => {
  const computeMonthTermFrequency = useMemo(() => {
    if (amountRequested && merchantId && paymentPlanId && creditRatingInterestRate && provinceIso && firstPaymentOn) {
      return createComputeMonthTermFrequency(
        merchantId,
        provinceIso,
        paymentPlanId,
        creditRatingInterestRate,
        amountRequested,
        includeInsurance,
        isJointCoverage,
        firstPaymentOn,
      )
    }
    return null
  }, [
    amountRequested,
    paymentPlanId,
    creditRatingInterestRate,
    merchantId,
    provinceIso,
    includeInsurance,
    isJointCoverage,
    firstPaymentOn,
  ])

  const [possibleTerms] = useGetPossibleTerms(computeMonthTermFrequency, amountRequested ?? 0)
  return possibleTerms
}

interface UseCalculatorTermAndPlanOptionsProps {
  merchantId: string
  provinceIso?: EProvince
  amountRequested?: number
  creditRatingMinErs?: number
  protectionPlan?: EProtectionPlanCoverage
  firstPaymentOn?: string
}

export const useCalculatorTermAndPlanOptions = ({
  merchantId,
  provinceIso,
  amountRequested,
  creditRatingMinErs,
  protectionPlan = EProtectionPlanCoverage.None,
  firstPaymentOn,
}: UseCalculatorTermAndPlanOptionsProps) => {
  const canPrice = !!merchantId && !!provinceIso && !!amountRequested && creditRatingMinErs != null && !!firstPaymentOn

  const regularRateRequest = useMemo(
    () =>
      canPrice
        ? createEstimateRequest(
            merchantId,
            provinceIso,
            amountRequested,
            EPaymentPlan.RegularDailyInterests,
            null,
            creditRatingMinErs,
            protectionPlan,
            firstPaymentOn,
          )
        : null,
    [canPrice, merchantId, provinceIso, amountRequested, creditRatingMinErs, protectionPlan, firstPaymentOn],
  )
  const [regularEstimate, isFetchingRegularRate] = useCalculatorEstimate(EFinancingProgram.Personal, regularRateRequest)
  const regularRate = isFetchingRegularRate ? undefined : regularEstimate?.interestRate

  const simulationRequest = useMemo(
    () =>
      canPrice && regularRate
        ? createTermPlanSimulationRequest(
            merchantId,
            provinceIso,
            amountRequested,
            regularRate,
            protectionPlan,
            firstPaymentOn,
          )
        : null,
    [canPrice, regularRate, merchantId, provinceIso, amountRequested, protectionPlan, firstPaymentOn],
  )
  const [rows, isFetchingRows] = useCalculateTermAndPlanOptionsSimulation(EFinancingProgram.Personal, simulationRequest)

  return useMappedTermAndPlanOptions(rows, isFetchingRows, !amountRequested)
}

interface UseTermAndPlanAutoSelectionProps {
  options: TermAndPlanOption[]
  paymentPlanId?: string
  term?: number
  setValue: UseFormSetValue<PersonalLoanCalculatorSchemaType>
}

export const useTermAndPlanAutoSelection = ({
  options,
  paymentPlanId,
  term,
  setValue,
}: UseTermAndPlanAutoSelectionProps) => {
  useEffect(() => {
    if (options.some((o) => !o.disabled && o.paymentPlanId === paymentPlanId && o.termMonths === term)) return
    const option = pickDefaultTermAndPlanOption(options)
    if (!option) return
    setValue('paymentPlanId', option.paymentPlanId as EPaymentPlan)
    setValue('term', option.termMonths as ETerms)
  }, [options, paymentPlanId, term, setValue])
}
